import React, { useState, useEffect } from 'react';
import {
    Box,
    Grid,
    Paper,
    Checkbox,
    FormControlLabel,
    IconButton,
    CircularProgress,
    Alert,
    Typography
} from '@mui/material';
import CustomTypography from '../../../components/UIComponents/CustomTypography';
import theme from '../../../assets/theme';
import CustomButton from '../../../components/UIComponents/CustomButton';
import StyledAutocomplete from '../../../components/UIComponents/StyledAutocomplete';
import StyledTextField from '../../../components/UIComponents/StyledTextField';
import { createPackage, fetchRoomTypes } from '../../../service/network';
import { useNotification } from "../../../context/notification.context"
import moment from 'moment';
import childIcon from "../../../assets/icons/child.png"
import AdultIcon from "../../../assets/icons/adult.png"
//icons
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import PersonIcon from '@mui/icons-material/Person';

const daysOfWeek = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const weekends = ['Fri', 'Sat', 'Sun'];



const NewPackageForm = ({ onSuccess, setShowNewPackageForm }) => {
    // Main form state
    const [formData, setFormData] = useState({
        packageName: '',
        packageCode: '',
        status: 'Active',
        // company: 'Mathiro Bliss',
        surchargeType: '₹'
    });
    const { showNotification } = useNotification();


    // Room types state (array of room configurations)
    const [roomTypes, setRoomTypes] = useState([
        {
            id: 1,
            roomType: '',
            fromDate: '',
            toDate: '',
            selectedDays: [...daysOfWeek],     // all days selected
            baseRate: '',
            adults: '',
            children: '',
            extraAdultCharge: '',
            extraChildCharge: '',
            weekendSurcharge: '',
            selectedWeekends: []
        }
    ]);

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    const allowOnlyPositiveNumbers = (value) => {
        // Allow only digits (0–9), optional decimal
        return value === '' || /^\d*\.?\d*$/.test(value);
    };

    // Handle changes in main form fields
    const handleFormChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // Handle changes in room type fields
    const handleRoomTypeChange = (id, field, value) => {
        setRoomTypes(prev =>
            prev.map(room =>
                room.id === id ? { ...room, [field]: value } : room
            )
        );
    };

    // Toggle day selection
    const toggleDay = (id, day) => {
        setRoomTypes(prev =>
            prev.map(room =>
                room.id === id
                    ? {
                        ...room,
                        selectedDays: room.selectedDays.includes(day)
                            ? room.selectedDays.filter(d => d !== day)
                            : [...room.selectedDays, day]
                    }
                    : room
            )
        );
    };

    // Toggle weekend selection
    const toggleWeekend = (id, day) => {
        setRoomTypes(prev =>
            prev.map(room =>
                room.id === id
                    ? {
                        ...room,
                        selectedWeekends: room.selectedWeekends.includes(day)
                            ? room.selectedWeekends.filter(d => d !== day)
                            : [...room.selectedWeekends, day]
                    }
                    : room
            )
        );
    };

    // Toggle all days selection
    const toggleAllDays = (id) => {
        setRoomTypes(prev =>
            prev.map(room =>
                room.id === id
                    ? {
                        ...room,
                        selectedDays: room.selectedDays.length === daysOfWeek.length ? [] : [...daysOfWeek]
                    }
                    : room
            )
        );
    };

 // Modified addRoomType function
const addRoomType = () => {
    // Get all currently selected room types
    const selectedRoomTypes = roomTypes.map(room => room.roomType);

    // Filter available options to only show unselected ones
    const availableOptions = roomTypeOptions.filter(
        option => !selectedRoomTypes.includes(option)
    );

    if (availableOptions.length === 0) {
        showNotification(
            'warning',
            'No Available Room Types',
            'All available room types have already been added to this package. ' +
            'Please remove an existing room type if you need to add a different one.'
        );
        return;
    }

    // Generate a unique ID - using timestamp to ensure uniqueness
    const newId = Date.now();

    setRoomTypes(prev => [
        ...prev,
        {
            id: newId, // Use unique ID instead of prev.length + 1
            roomType: '',
            fromDate: '',
            toDate: '',
            selectedDays: [],
            baseRate: '',
            adults: '',
            children: '',
            extraAdultCharge: '',
            extraChildCharge: '',
            weekendSurcharge: '',
            selectedWeekends: []
        }
    ]);
};

// Modified removeRoomType function (no changes needed to this one)
const removeRoomType = (id) => {
    setRoomTypes(prev => prev.filter(room => room.id !== id));
};
    const [roomTypeOptions, setRoomTypeOptions] = useState([]);

    useEffect(() => {
        const loadRoomTypes = async () => {
            try {
                const params = {
                    doctype: 'Room Type',
                    fields: JSON.stringify(["name", "room_type"])
                };
                const res = await fetchRoomTypes(params);
                const roomTypes = res.data.message.values.map(item => item[1]);
                setRoomTypeOptions([...new Set(roomTypes)]);
            } catch (err) {
                console.error("Failed to fetch room types", err);
            }
        };

        loadRoomTypes();
    }, []);


    const handleSubmit = async () => {
        setIsLoading(true);
        setError(null);
        setSuccess(false);

        try {
            // Check for duplicate room types
            const roomTypeValues = roomTypes.map(room => room.roomType);
            const uniqueRoomTypes = [...new Set(roomTypeValues)];
            if (roomTypeValues.length !== uniqueRoomTypes.length) {
                throw new Error('Duplicate room types are not allowed in the same package');
            }

            if (!formData.packageName || !formData.packageCode) {
                throw new Error('Package name and code are required');
            }

            const packageDetails = roomTypes.map(room => {
                if (!room.roomType || !room.fromDate || !room.toDate || room.selectedDays.length === 0) {
                    throw new Error('All room type fields are required');
                }

                return {
                    doctype: "Package Details",
                    surcharge_method: formData.surchargeType,
                    parenttype: "Room Package",
                    room_type: room.roomType,
                    no_of_adult: parseInt(room.adults) || 0,
                    no_of_child: parseInt(room.children) || 0,
                    rate_per_night: parseFloat(room.baseRate) || 0,
                    from_date: room.fromDate,
                    to_date: room.toDate,
                    applicable_days: JSON.stringify(room.selectedDays.map(day => day.toLowerCase())),
                    extra_child_charge: parseFloat(room.extraChildCharge) || 0,
                    extra_adult_charge: parseFloat(room.extraAdultCharge) || 0,
                    surcharge: parseFloat(room.weekendSurcharge) || 0,
                    weekend_days: JSON.stringify(room.selectedWeekends.map(day => day.toLowerCase())),
                };
            });

            const packageData = {
                doc: JSON.stringify({
                    doctype: "Room Package",
                    naming_series: "PK.-YYYY.-",
                    package_details: packageDetails,
                    status: formData.status,
                    // company: formData.company,
                    package_name: formData.packageName,
                    package_code: formData.packageCode
                }),
                action: "Save"
            };

            const response = await createPackage(packageData);


            if (response && (response.data || response.status === 200 || response.status === 201)) {
                showNotification('success', 'Success', 'Package created successfully.');
                setSuccess(true);


                // Reset form state
                setFormData({
                    packageName: '',
                    packageCode: '',
                    status: 'Active',
                    // company: 'Mathiro Bliss',
                    surchargeType: '₹'
                });

                setRoomTypes([{
                    id: 1,
                    roomType: '',
                    fromDate: '',
                    toDate: '',
                    selectedDays: [],
                    baseRate: '',
                    adults: '',
                    children: '',
                    extraAdultCharge: '',
                    extraChildCharge: '',
                    weekendSurcharge: '',
                    selectedWeekends: []
                }]);

                // Call onSuccess to close the form
                if (onSuccess) onSuccess();

            } else {
                throw new Error('Failed to create package');
            }
        } catch (err) {
            console.error('Error creating package:', err);
            let errorMessage = err.message || 'Failed to create package.';

            if (err.response?.data?._server_messages) {
                try {
                    const messages = JSON.parse(err.response.data._server_messages)
                        .map(msg => JSON.parse(msg).message.replace(/<[^>]+>/g, ''))
                        .join(', ');
                    errorMessage = messages;
                } catch (parseErr) {
                    console.error('Error parsing server messages:', parseErr);
                }
            } else if (err.response?.data?.message) {
                errorMessage = err.response.data.message;
            }

            showNotification('error', 'Error', errorMessage);
            setError(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };
    return (
        <Box
            sx={{
                position: 'relative', // Make relative to allow absolute positioning inside
                backgroundColor: theme.palette.light.main,
                borderRadius: theme.borderRadius.xl,
                boxShadow: '0px 4px 12px 0px rgba(74, 76, 78, 0.25)',
                p: 2.5,
                width: '100%',
                maxHeight: 'fit-content',
                mt: 2,
                backgroundColor: 'light.main',
            }}
        >
            {/* Close Button */}
            <IconButton
                sx={{
                    position: 'absolute',
                    top: -5,
                    right: -5,
                    backgroundColor: 'error.main',
                    color: 'light.main',
                    padding: '6px',
                    width: 20,
                    height: 20,
                    '&:hover': {
                        backgroundColor: 'error.dark',
                    },
                    zIndex: 1,
                }}
                onClick={() => setShowNewPackageForm(false)}
            >
                <CloseRoundedIcon fontSize="small" />
            </IconButton>
            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
            >
                <Box sx={{ width: '100%', mb: 2 }}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid size={2}>
                            <StyledTextField
                                fullWidth
                                label="Package Name"
                                placeholder="Enter the Package Name"
                                value={formData.packageName}
                                onChange={(e) => handleFormChange('packageName', e.target.value)}
                            />
                        </Grid>

                        <Grid size={2}>
                            <StyledTextField
                                fullWidth
                                label="Package Code"
                                placeholder="Enter the Code"
                                value={formData.packageCode}
                                onChange={(e) => handleFormChange('packageCode', e.target.value)}
                            />
                        </Grid>

                        <Grid size={1.5}>
                            <StyledAutocomplete
                                label="Status"
                                options={['Active', 'Inactive']}
                                value={formData.status}
                                onChange={(event, newValue) => handleFormChange('status', newValue)}
                                fullWidth
                            />
                        </Grid>
                    </Grid>
                </Box>

                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    {/* Form fields like Package Name, Code, Status here... */}

                    <Box display="flex" gap={1} minWidth={2}>
                        {/* Save Button */}
                        <IconButton
                            onClick={handleSubmit}
                            disabled={isLoading}
                            sx={{
                                width: 30,
                                height: 30,
                                borderRadius: '4px',
                                padding: '2px',
                                border: '1px solid #84AFDF', // Keep only this
                                backgroundColor: 'lightblue.main',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >

                            {isLoading ? (
                                <CircularProgress size={18} color="inherit" />
                            ) : (
                                <SaveOutlinedIcon sx={{ color: 'primary.main', width: 24, }} />
                            )}
                        </IconButton>


                    </Box>
                </Box>


            </Box>

            {roomTypes.map((roomType) => (
                <Box
                    key={roomType.id}
                    sx={{
                        border: '1px solid #CFCFCF',
                        borderRadius: 2,
                        p: 2,
                        mt: 2,
                        backgroundColor: 'light.main',
                        position: 'relative'
                    }}
                >
                    {roomTypes.length > 1 && (
                        <IconButton
                            sx={{
                                position: 'absolute',
                                top: 0,
                                right: 0,
                                m: -1,
                                backgroundColor: 'error.main',
                                color: 'light.main',
                                padding: '6px',
                                width: 20,
                                height: 20,
                                '&:hover': {
                                    backgroundColor: 'error.dark',
                                },
                                zIndex: 1,
                            }}
                            onClick={() => removeRoomType(roomType.id)}
                        >
                            <CloseRoundedIcon fontSize="small" />
                        </IconButton>
                    )}

                    <Grid container spacing={2} alignItems="center">
                        <Grid size={2}>
                            <Box>

                                <StyledAutocomplete
                                    label="Room Type"
                                    options={roomTypeOptions.filter(
                                        option => !roomTypes.some(
                                            room => room.id !== roomType.id && room.roomType === option
                                        )
                                    )}
                                    value={roomType.roomType}
                                    onChange={(e, newValue) => handleRoomTypeChange(roomType.id, 'roomType', newValue)}
                                    size="small"
                                    disableClearable
                                    renderInput={(params) => (
                                        <StyledTextField {...params} placeholder="Select" />
                                    )}
                                    componentsProps={{
                                paper: {
                                    sx: {
                                        borderRadius: '8px',
                                        backgroundColor: 'lightred.main',
                                        '& .MuiAutocomplete-option:hover': {
                                            backgroundColor: '#FFD4D1',
                                        },
                                    },
                                },
                            }}
                                />

                            </Box>
                        </Grid>

                        <Grid size={1.5}>
                            <StyledTextField
                                label="Validity Period"
                                type="date"
                                InputLabelProps={{ shrink: true }}
                                value={roomType.fromDate}
                                onChange={(e) => handleRoomTypeChange(roomType.id, 'fromDate', e.target.value)}
                                error={roomType.toDate && roomType.fromDate > roomType.toDate}

                            />
                        </Grid>

                        <Grid size={1.5} mt={2.8} ml={-1}>
                            <StyledTextField
                                type="date"
                                InputLabelProps={{ shrink: true }}
                                value={roomType.toDate}
                                onChange={(e) => handleRoomTypeChange(roomType.id, 'toDate', e.target.value)}
                                InputProps={{
                                    inputProps: {
                                        min: roomType.fromDate || moment().format("YYYY-MM-DD"),
                                    },
                                }}
                                error={roomType.fromDate && roomType.toDate < roomType.fromDate}

                            />
                        </Grid>


                        <Grid size={5}>

                            <Box>
                                <CustomTypography
                                    variant="bodyText1"
                                    display="block"
                                    gutterBottom
                                    color="dark.main"
                                >
                                    Applicable Days
                                </CustomTypography>

                                <Box
                                    display="flex"
                                    flexWrap="nowrap" // Changed from wrap to nowrap for horizontal scrolling
                                    gap={1}
                                    sx={{
                                        border: '1px solid #CFCFCF',
                                        borderRadius: 2,
                                        backgroundColor: 'light.main',
                                        pl: 1,
                                        height: 40,
                                        overflowX: 'auto', // Horizontal scrolling only
                                        overflowY: 'hidden', // Hide vertical scrollbar
                                        whiteSpace: 'nowrap', // Prevent wrapping
                                        '&::-webkit-scrollbar': {
                                            height: '6px', // Make scrollbar thinner
                                        },
                                        '&::-webkit-scrollbar-thumb': {
                                            backgroundColor: '#CFCFCF',
                                            borderRadius: '3px',
                                        },
                                    }}
                                >
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={roomType.selectedDays.length === daysOfWeek.length}
                                                onChange={() => toggleAllDays(roomType.id)}
                                                sx={{
                                                    color: 'primary.main',
                                                    '&.Mui-checked': {
                                                        color: 'primary.main',
                                                    },
                                                    borderRadius: '4px',
                                                }}
                                            />
                                        }
                                        label="All"
                                        sx={{ mr: 0, flexShrink: 0 }} // Prevent shrinking
                                    />
                                    {daysOfWeek.map((day) => (
                                        <FormControlLabel
                                            key={day}
                                            control={
                                                <Checkbox
                                                    checked={roomType.selectedDays.includes(day)}
                                                    onChange={() => toggleDay(roomType.id, day)}
                                                    sx={{
                                                        color: 'primary.main',
                                                        '&.Mui-checked': {
                                                            color: 'primary.main',
                                                        },
                                                        borderRadius: 4,
                                                    }}
                                                />
                                            }
                                            label={day}
                                            sx={{ mr: 2, flexShrink: 0 }} // Prevent shrinking
                                        />
                                    ))}
                                </Box>
                            </Box>
                        </Grid>


                        <Grid size={2}>
                            <StyledTextField
                                label="Base Rate / Night"
                                placeholder="Enter Amount"
                                value={roomType.baseRate}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    if (allowOnlyPositiveNumbers(val)) {
                                        handleRoomTypeChange(roomType.id, 'baseRate', val);
                                    }
                                }}
                                inputProps={{
                                    style: {
                                        textAlign: 'right',
                                        // You can add other styles if needed
                                    }
                                }}
                            />
                        </Grid>

                        <Grid size={1.5}>
                            <Typography variant='bodyText1' color='dark.main'>Occupancy Limit</Typography>
                            <Box display="flex" alignItems="center" gap={1}>
                                <img src={AdultIcon} width={22} />
                                <StyledTextField
                                    placeholder="No of Adults"
                                    value={roomType.adults}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        if (allowOnlyPositiveNumbers(val)) {
                                            handleRoomTypeChange(roomType.id, 'adults', val);
                                        }
                                    }}
                                    fullWidth
                                />
                            </Box>
                        </Grid>

                        <Grid size={1.5} ml={-1.2} mt={3.2}>
                            <Box display="flex" alignItems="center" gap={1}>
                                <img src={childIcon} width={22} />
                                <StyledTextField
                                    placeholder="No of Child"
                                    value={roomType.children}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        if (allowOnlyPositiveNumbers(val)) {
                                            handleRoomTypeChange(roomType.id, 'children', val);
                                        }
                                    }}
                                    fullWidth
                                />
                            </Box>
                        </Grid>


                        <Grid size={1.5}>
                            <StyledTextField
                                label="Extra Adult Charge"
                                placeholder='Enter Amount'
                                value={roomType.extraAdultCharge}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    if (allowOnlyPositiveNumbers(val)) {
                                        handleRoomTypeChange(roomType.id, 'extraAdultCharge', val);
                                    }
                                }}
                            />
                        </Grid>

                        <Grid size={1.5}>
                            <StyledTextField
                                label="Extra Child Charge"
                                placeholder='Enter Amount'
                                value={roomType.extraChildCharge}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    if (allowOnlyPositiveNumbers(val)) {
                                        handleRoomTypeChange(roomType.id, 'extraChildCharge', val);
                                    }
                                }}
                            />
                        </Grid>

                        <Grid size={1.5}>
                            <Box>

                                <StyledTextField
                                    fullWidth
                                    label="Weekend Surcharge"
                                    placeholder='Enter Amount'
                                    size="small"
                                    value={roomType.weekendSurcharge}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        if (allowOnlyPositiveNumbers(val)) {
                                            handleRoomTypeChange(roomType.id, 'weekendSurcharge', val);
                                        }
                                    }}
                                    
                                />
                            </Box>
                        </Grid>

                        <Grid size={0.8} mt={3} ml={-1}>
                            <StyledAutocomplete
                                options={['%', '₹']}
                                value={formData.surchargeType}
                                onChange={(e, newValue) => handleFormChange('surchargeType', newValue)}
                                size="small"
                                disableClearable
                                renderInput={(params) => (
                                    <StyledTextField {...params} placeholder='₹' />
                                )}
                             componentsProps={{
                                paper: {
                                    sx: {
                                        borderRadius: '8px',
                                        backgroundColor: 'lightred.main',
                                        '& .MuiAutocomplete-option:hover': {
                                            backgroundColor: '#FFD4D1',
                                        },
                                    },
                                },
                            }}
                            />
                        </Grid>
                        <Grid size={2}>
                            <Box>
                                <CustomTypography variant="bodyText1" display="block" gutterBottom color="dark.main">
                                    Choose Weekend
                                </CustomTypography>
                                <Box display="flex" gap={1}
                                    sx={{
                                        border: '1px solid #CFCFCF',
                                        borderRadius: 2,
                                        height: 40,
                                        backgroundColor: 'light.main',
                                        minWidth: 260,
                                        pl: 1, // optional padding
                                    }}>
                                    {weekends.map((day) => (
                                        <FormControlLabel
                                            key={day}
                                            control={
                                                <Checkbox
                                                    checked={roomType.selectedWeekends.includes(day)}
                                                    onChange={() => toggleWeekend(roomType.id, day)}
                                                    sx={{
                                                        color: 'primary.main',
                                                        '&.Mui-checked': {
                                                            color: 'primary.main',
                                                        },
                                                        borderRadius: 4,
                                                    }}
                                                />
                                            }
                                            label={day}
                                        />
                                    ))}
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                </Box>
            ))}

            <Box mt={2} display="flex" justifyContent="flex-start">
                <CustomButton
                    variant="outlined"
                    sx={{
                        borderColor: 'primary.main',
                        color: 'primary.main',
                        borderRadius: theme.borderRadius.medium,
                        border: "2px solid var(--Primary-Blue-MB, #27394D)",
                        textTransform: 'none',
                        '&:hover': { borderColor: 'primary.dark' },
                    }}
                    onClick={addRoomType}
                >
                    + Room Type
                </CustomButton>
            </Box>
        </Box>
    );
};

export default NewPackageForm;